import { useEffect, useState } from "react";
import { useLocalization } from "@fluent/react";
import { Button, LoadingSkeleton, MenuButton, Row } from "@narmi/design_system";
import {
  ContentCard,
  useLoadingContext,
  useNotificationContext,
} from "cerulean";
import Payee, { ErrorMessage } from "byzantine/src/Payee";
import Filters from "byzantine/src/filters";
import AddPayeeModal from "../../transfer/AddPayeeModal";
import DeleteDialog from "./DeleteDialog";
import { useCurrentUser } from "../../contexts/CurrentUserContext";

interface PayeeOptionsProps {
  openDeleteDialog: () => void;
}

const PayeeOptions = ({ openDeleteDialog }: PayeeOptionsProps) => (
  <MenuButton triggerIcon="more-vertical">
    <MenuButton.Item
      label="Delete"
      startIcon="trash-2"
      onSelect={openDeleteDialog}
    />
  </MenuButton>
);

interface PayeeCardProps {
  payee: Payee;
  fetchPayees: () => Promise<void>;
}

const PayeeCard = ({ payee, fetchPayees }: PayeeCardProps) => {
  const { l10n } = useLocalization();
  const [isDeletePayeeDialogOpen, setIsDeletePayeeDialogOpen] = useState(false);
  const { sendNotification } = useNotificationContext();
  const { setIsLoading } = useLoadingContext();
  const { currentUser } = useCurrentUser();

  const deletePayee = async () => {
    setIsDeletePayeeDialogOpen(false);
    setIsLoading(true);
    try {
      await payee.delete();
      setIsLoading(false);
      sendNotification({
        type: "success",
        text: l10n.getString("notification-payee-deleted"),
      });
      await fetchPayees();
    } catch (error: unknown) {
      const e = error as ErrorMessage;
      let toastMessage = l10n.getString("error-payee-delete");
      if (e?.reason?.toLowerCase() === Payee.ERROR.PENDING_PAYMENTS_EXIST) {
        toastMessage = l10n.getString("error-payee-delete-pending-payments");
      }
      sendNotification({
        type: "negative",
        text: toastMessage,
      });
      setIsLoading(false);
    }
  };

  return (
    <>
      <ContentCard>
        <div
          style={{ display: "flex", flexDirection: "column", height: "100%" }}
        >
          <Row>
            <Row.Item>
              <div className="fontColor--heading fontWeight--semibold whiteSpace--truncate--multiLine--2">
                {Filters.titlecase(payee.name)}
              </div>
            </Row.Item>
            {currentUser?.features?.["integrations.base.Payee_permission"] && (
              <Row.Item shrink>
                <div style={{ marginTop: "-4px" }}>
                  <PayeeOptions
                    openDeleteDialog={() => setIsDeletePayeeDialogOpen(true)}
                  />
                </div>
              </Row.Item>
            )}
          </Row>
          <div
            className="margin--top--xxs margin--bottom--s"
            style={{ height: "20px" }}
          >
            {payee.isPersonType() ? payee?.email : payee?.masked_account_number}
          </div>
          {payee?.description && (
            <div
              className="fontSize--s fontColor--secondary"
              style={{ marginTop: "auto" }}
            >
              {payee.description}
            </div>
          )}
        </div>
      </ContentCard>
      <DeleteDialog
        recipientName={Filters.titlecase(payee.name)}
        recipientType="payee"
        onDelete={deletePayee}
        isOpen={isDeletePayeeDialogOpen}
        closeDialog={() => setIsDeletePayeeDialogOpen(false)}
      />
    </>
  );
};

const PayeeSection = () => {
  const { l10n } = useLocalization();
  const [payees, setPayees] = useState<Payee[]>([]);
  const [isAddPayeeDialogOpen, setIsAddPayeeDialogOpen] = useState(false);
  const [isFetchingPayees, setIsFetchingPayees] = useState(false);
  const { currentUser } = useCurrentUser();

  const { sendNotification } = useNotificationContext();

  const fetchPayees = async (payee?: Payee) => {
    setIsFetchingPayees(true);
    try {
      setPayees((await Payee.fetchPayees(payee)) ?? []);
    } catch {
      sendNotification({
        type: "negative",
        text: l10n.getString("error-payee-fetch"),
      });
    } finally {
      setIsFetchingPayees(false);
    }
  };

  useEffect(() => {
    fetchPayees();
  }, []);

  return (
    <>
      <div className="margin--top--xl margin--bottom--l">
        <Row>
          <Row.Item>
            <div className="fontSize--l fontColor--heading fontWeight--bold">
              {l10n.getString("heading-payees")}
            </div>
          </Row.Item>
          {currentUser?.features?.["integrations.base.Payee_permission"] && (
            <Row.Item shrink>
              <Button
                kind="plain"
                label={l10n.getString("button-add-payee")}
                onClick={() => setIsAddPayeeDialogOpen(true)}
              />
            </Row.Item>
          )}
        </Row>
      </div>
      <LoadingSkeleton content="paragraph" isLoading={isFetchingPayees}>
        <div
          className={payees?.length ? "recipients-container" : "no-recipients"}
        >
          {payees?.map((payee) => (
            <PayeeCard key={payee.id} payee={payee} fetchPayees={fetchPayees} />
          ))}
          {!payees?.length && <div>{l10n.getString("message-no-payees")}</div>}
        </div>
      </LoadingSkeleton>
      <AddPayeeModal
        open={isAddPayeeDialogOpen}
        handleClose={() => setIsAddPayeeDialogOpen(false)}
        updatePayees={(newPayee: Payee) => {
          sendNotification({
            type: "success",
            text: l10n.getString("notification-payee-added"),
          });
          fetchPayees(newPayee);
        }}
      />
    </>
  );
};

export default PayeeSection;
